import { customerMoneyV1014 } from './customerMoneyV1014';
import { retryAuditSafeV1021, retrySummarySafeV1021 } from './retryAuditV1021';

export const CUSTOMER_LOG_TEXT_V822 = {
  include_archives_v119: ['包含历史归档', 'Include archived logs'],
  archive_help_v119: ['仅按所选日期读取归档；首次解压可能较慢，日期范围最多一年。', 'Only selected archive months are read. Initial decompression may take longer; select at most one year.'],
  recent_help_v119: ['当前仅查询在线日志，至少保留最近60天。更早记录请勾选“包含历史归档”。', 'Searching online logs only, retaining at least 60 days. Enable archived logs for older records.'],
  ordinary: ['日志', 'Logs'],
  special: ['专属客户日志', 'Dedicated customer logs'],
  unknown: ['未知身份 / 历史诊断', 'Unknown Identity / Legacy Diagnostics'],
  classification: ['日志视图', 'Log view'],
  root_required: [
    '仅 Root 可查看专属客户全局日志',
    'Root access is required for global dedicated customer logs',
  ],
  provider: ['Provider', 'Provider'],
  channel_provider: ['渠道 / Provider', 'Channel / Provider'],
  channel_provider_placeholder: [
    '渠道名称、ID 或 Provider',
    'Channel name, ID or provider',
  ],
  request_id: ['请求 ID', 'Request ID'],
  resolution: ['分辨率', 'Resolution'],
  actual_model: ['实际模型', 'Actual model'],
  structured: ['结构化请求诊断', 'Structured request diagnostics'],
  request: ['完整请求字段', 'Full request fields'],
  attempts: ['请求尝试', 'Request attempts'],
  google: ['Google 历史诊断', 'Legacy Google diagnostics'],
  billing: ['成本与收费诊断', 'Cost and charge diagnostics'],
  meters: ['计量明细', 'Usage meters'],
  meter: ['计量项', 'Meter'],
  quantity: ['数量', 'Quantity'],
  unit: ['单位', 'Unit'],
  measurement_status: ['计量状态', 'Measurement status'],
  source: ['来源', 'Source'],
  attributes: ['属性', 'Attributes'],
  no_meters: [
    '没有可用计量数据；未知不代表零',
    'No usage measurements available; unknown is not zero',
  ],
  detail_failed: [
    '结构化详情加载失败；原始日志仍保留',
    'Structured details could not be loaded; the original log is preserved',
  ],
  retry: ['重试', 'Retry'],
  copy_json: ['复制完整脱敏 JSON', 'Copy full sanitized JSON'],
  loading: ['正在加载详情', 'Loading details'],
  client_ip: ['请求 IP', 'Request IP'],
  ip_not_recorded: ['未记录', 'Not recorded'],
  ip_unavailable: ['暂不可用', 'Unavailable'],
};

export const customerLogTextV822 = (translate, key) => {
  const fullKey = `customer_logs_v822.${key}`;
  const words = CUSTOMER_LOG_TEXT_V822[key];
  const fallback = words ? [...new Set(words)].join(' / ') : '-';
  const translated = translate(fullKey, { defaultValue: fallback });
  return !translated || translated === fullKey ? fallback : translated;
};

export const customerLogStorageV822 = (scope, admin) => {
  const identity = `${scope}.${admin ? 'admin' : 'self'}`;
  return {
    session: `customer-logs.v8.22.${identity}`,
    columns: `alfredapi.log.visible_columns.v8.22.${identity}`,
    widths: `alfredapi.log.column_widths.v8.22.${identity}`,
  };
};

export const customerLogEndpointsV822 = (scope, admin) => {
  const self =
    scope === 'special' ? '/api/customer_logs_v822/self' : '/api/log/self';
  return {
    list: admin ? '/api/log/' : scope === 'special' ? self : `${self}/`,
    stat: admin ? '/api/log/stat' : `${self}/stat`,
    detail: (requestID) =>
      admin
        ? `/api/log/request_v822/${encodeURIComponent(requestID)}`
        : scope === 'special'
        ? `${self}/request/${encodeURIComponent(requestID)}`
        : `${self}/request_v105/${encodeURIComponent(requestID)}`,
  };
};

export const customerLogParamsV822 = (params, scope, admin) => {
  const result = {
    ...params,
    customer_scope: scope === 'unknown' && !admin ? 'ordinary' : scope,
    provider_family: params.provider_family || '',
    request_id: params.request_id || '',
  };
  if (!admin) {
    if (scope !== 'ordinary') delete result.username;
    delete result.billing_account_id;
    delete result.channel;
  }
  return result;
};

export const customerLogValueV822 = (value) =>
  value === null ||
  value === undefined ||
  value === '' ||
  (typeof value === 'number' && !Number.isFinite(value))
    ? '-'
    : String(value);

export const customerLogUsageV822 = (log, field) =>
  log?.has_missing_usage ||
  log?.usage_source === 'missing' ||
  ['missing', 'unknown', 'unavailable'].includes(log?.measurement_status)
    ? '-'
    : customerLogValueV822(log?.[field]);

export const customerLogAmountV822 = (
  log,
  renderLegacy = customerLogValueV822
) => {
  if (['incomplete', 'unavailable'].includes(log?.billing_status)) return '-';
  const money = customerMoneyV1014(log);
  if (Object.prototype.hasOwnProperty.call(log || {}, 'money')) {
    const amount = money?.wallet_debit_usd ?? money?.priced_usd;
    return amount == null ? '-' : `USD ${amount}`;
  }
  if (
    log?.billing_status !== 'legacy' &&
    (Number(log?.billing_account_id) > 0 || log?.billing_status)
  ) {
    if (
      [
        'pending',
        'pending_price',
        'pending_receipt',
        'needs_review',
        'unavailable',
        'unknown',
      ].includes(log.billing_status)
    )
      return '-';
    const amount = customerLogValueV822(log.customer_charge_usd);
    return amount === '-' ? '-' : `USD ${amount}`;
  }
  return customerLogValueV822(log?.quota) === '-'
    ? '-'
    : renderLegacy(log.quota);
};

const secretFieldV822 =
  /^(?:api[_-]?key|key|authorization|proxy[_-]?authorization|private[_-]?key|access[_-]?token|refresh[_-]?token|id[_-]?token|password|passwd|secret|session[_-]?secret|client[_-]?secret|credentials?|cookie|set-cookie)$/i;

export const sanitizeCustomerLogV822 = (value) => {
  if (Array.isArray(value)) return value.map(sanitizeCustomerLogV822);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [
        key,
        secretFieldV822.test(key)
          ? '[REDACTED]'
          : sanitizeCustomerLogV822(entry),
      ])
    );
  }
  if (typeof value !== 'string') return value;
  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === 'object')
      return JSON.stringify(sanitizeCustomerLogV822(parsed));
  } catch {}
  return value
    .replace(/Bearer\s+[A-Za-z0-9._~+/-]+=*/gi, 'Bearer [REDACTED]')
    .replace(
      /((?:api[_-]?key|authorization|password|client_secret)\s*[=:]\s*)[^\s,;&]+/gi,
      '$1[REDACTED]'
    );
};

const SAFE_REQUEST_FIELDS_V822 = new Set([
  'id',
  'request_id',
  'created_at',
  'request_received_at',
  'type',
  'outcome',
  'http_status',
  'content',
  'username',
  'user_id',
  'token_name',
  'model_name',
  'requested_model_name',
  'provider_family',
  'customer_scope',
  'resolution',
  'actual_model',
  'service_tier',
  'cached_input_tokens',
  'cache_creation_input_tokens',
  'reasoning_tokens',
  'accepted_prediction_tokens',
  'rejected_prediction_tokens',
  'legacy_amount_usd',
  'quota',
  'prompt_tokens',
  'completion_tokens',
  'total_tokens',
  'elapsed_time',
  'first_response_time',
  'is_stream',
  'error_code',
  'billing_status',
  'customer_charge_usd',
  'usage_source',
  'has_missing_usage',
  'has_fallback_usage',
  'measurement_status',
  'is_error',
  'log_debug_enabled',
  'source_id',
  'source_name',
  'source_kind',
  'source_multiplier',
  'effective_multiplier',
  'markup_multiplier',
  'billing_mode',
  'receipt_status',
  'monthly_payable_basis_points',
  'price_is_estimate',
  'money',
  'settlement_method',
  'retry_summary_v1021',
]);

export const customerLogSafeRequestV822 = (request) =>
  sanitizeCustomerLogV822(
    Object.fromEntries(
      Object.entries(request || {})
        .filter(([key]) => SAFE_REQUEST_FIELDS_V822.has(key))
        .map(([key, value]) => [
          key,
          key === 'money'
            ? customerMoneyV1014(request)
            : key === 'retry_summary_v1021'
            ? retrySummarySafeV1021(value, request?.request_id)
            : value,
        ])
    )
  );

export const customerLogDetailV822 = (detail, original, admin) => {
  if (admin)
    return sanitizeCustomerLogV822({
      ...detail,
      ...(detail?.retry_audit_v1021
        ? {
            retry_audit_v1021: retryAuditSafeV1021(
              detail.retry_audit_v1021,
              detail?.request?.request_id || original?.request_id
            ),
          }
        : {}),
      request: {
        ...original,
        ...detail?.request,
        ...(detail?.request?.retry_audit_v1021 || original?.retry_audit_v1021
          ? {
              retry_audit_v1021: retryAuditSafeV1021(
                detail?.request?.retry_audit_v1021 ||
                  original.retry_audit_v1021,
                detail?.request?.request_id || original?.request_id
              ),
            }
          : {}),
        ...(detail?.request?.retry_summary_v1021 ||
        original?.retry_summary_v1021
          ? {
              retry_summary_v1021: retrySummarySafeV1021(
                detail?.request?.retry_summary_v1021 ||
                  original.retry_summary_v1021,
                detail?.request?.request_id || original?.request_id
              ),
            }
          : {}),
      },
    });
  return {
    ...(detail?.client_ip_status
      ? {
          client_ip: detail.client_ip ?? null,
          client_ip_status: detail.client_ip_status,
          ...(detail.client_ip_source
            ? { client_ip_source: detail.client_ip_source }
            : {}),
        }
      : {}),
    request: customerLogSafeRequestV822(detail?.request || original),
    ...(detail?.retry_audit_v1021
      ? {
          retry_audit_v1021: retryAuditSafeV1021(
            detail.retry_audit_v1021,
            detail?.request?.request_id || original?.request_id
          ),
        }
      : {}),
    meters: sanitizeCustomerLogV822(
      (detail?.meters || []).map((meter) => ({
        meter: meter.meter,
        quantity: meter.quantity,
        unit: meter.unit,
        measurement_status: meter.measurement_status,
        source: meter.source,
        attributes: meter.attributes,
      }))
    ),
  };
};
